/**
 * menusApi — the builder's typed READ client for the store's navigation menus, used to populate the
 * `menu` field's picker (Shopify `link_list`: the merchant picks WHICH menu a footer/header block shows).
 * Reuses the authed `apiRequest` (Bearer from the JWT; `store_id` is derived server-side — no tenant
 * surface). Read-only; the menus themselves are authored in dashboard → Navigation.
 */
import type { LinkValue, MenuEntry } from '@aquilixshop/ui-react';
import { apiRequest } from './http';

export interface MenuSummary {
  id: string;
  handle: string;
  title: string;
  itemCount: number;
}

interface MenuListResponse {
  menus: ReadonlyArray<{
    id: string;
    handle: string;
    title: string;
    itemCount: number;
    updatedAt: string;
  }>;
}

/** The store's menus (summaries) for the picker dropdown. */
export async function listMenus(): Promise<MenuSummary[]> {
  const res = await apiRequest<MenuListResponse>('/api/store/menus');
  return res.menus.map((m) => ({
    id: m.id,
    handle: m.handle,
    title: m.title,
    itemCount: m.itemCount,
  }));
}

// ── Menu ITEMS (the canvas nav preview) ───────────────────────────────────────────────────────────
// The picker above only needs handles + titles. The CANVAS needs the items: the header/footer read
// `RenderContext.resolved.menus[handle]`, which the storefront fills from the page bundle. The builder
// has no such bundle, so it fetches the picked menus itself (see `useResolvedMenus`). Without this the
// canvas rendered an EMPTY nav the moment a seller picked a menu — the section's `link` blocks are only
// the fallback for an UNPICKED menu, so a picked-but-unresolved handle showed nothing at all.

interface MenuLinkDto {
  type: string;
  slug: string | null;
  url: string | null;
  newTab: boolean;
}
interface MenuItemDto {
  id: string;
  label: string;
  link: MenuLinkDto;
  children: readonly MenuItemDto[];
}
interface MenuDetailResponse {
  id: string;
  handle: string;
  title: string;
  items: readonly MenuItemDto[];
  version: number;
}

/** One menu's items, in the render shape the chrome reads. Throws like any `apiRequest`. */
export async function getMenuEntries(id: string): Promise<readonly MenuEntry[]> {
  const res = await apiRequest<MenuDetailResponse>(`/api/store/menus/${encodeURIComponent(id)}`);
  return (res.items ?? []).map(toMenuEntry);
}

function toMenuEntry(item: MenuItemDto): MenuEntry {
  return {
    label: item.label,
    link: toLinkValue(item.link),
    children: (item.children ?? []).map(toMenuEntry),
  };
}

/** Mirror of the storefront's `toLinkValue` — the same wire shape must resolve to the same href. */
function toLinkValue(l: MenuLinkDto): LinkValue {
  const newTab = l.newTab;
  switch (l.type) {
    case 'product':
      return { type: 'product', slug: l.slug ?? '', newTab };
    case 'collection':
      return { type: 'collection', slug: l.slug ?? '', newTab };
    case 'page':
      return { type: 'page', slug: l.slug ?? '', newTab };
    case 'catalog':
      return { type: 'catalog', newTab };
    case 'policy':
      return { type: 'policy', slug: l.slug ?? '', newTab };
    default:
      return { type: 'external', url: l.url ?? '#', newTab };
  }
}
